/**
 * League Helpers
 *
 * One vocabulary for the `league` column and one set of tests over it. Until
 * Sep 2026 five files each kept their own list and half a dozen more did
 * their own substring checks, so "College", "NCAA" and "College - D1" were
 * three different leagues to different parts of the app.
 */

/**
 * The values a client's `league` may hold. The client form, the MCP
 * create/update tools and the bulk importer all draw from this list.
 * Existing rows may still carry older spellings ("NCAA", "College"); the
 * predicates below accept those on read.
 */
export const LEAGUE_OPTIONS = [
  'NFL', 'UFL', 'CFL', 'NBA', 'MLB', 'NHL', 'MLS', 'WNBA', 'PGA',
  'College - D1', 'College - D2', 'College - D3', 'NAIA', 'JUCO',
  'High School', 'International', 'Other',
] as const

export type LeagueOption = (typeof LEAGUE_OPTIONS)[number]

const PRO_LEAGUES = ['nfl', 'ufl', 'cfl', 'nba', 'mlb', 'nhl', 'mls', 'wnba', 'pga']

/** Returns true for professional leagues (NFL, NBA, MLB, etc.) */
export function isProLeague(league: string | null | undefined): boolean {
  if (!league) return false
  const lower = league.toLowerCase()
  return PRO_LEAGUES.some(l => lower === l || lower.includes(l))
}

/**
 * Exactly the NFL. Market percentiles, cap figures and the OverTheCap
 * comparables exist for this league alone, so anything that prints one of
 * those must gate on this, not on isProLeague.
 */
export function isNflLeague(league: string | null | undefined): boolean {
  return String(league ?? '').trim().toUpperCase() === 'NFL'
}

/**
 * Any college level: NCAA divisions, NAIA, junior college, and the older
 * spellings still on existing rows.
 */
export function isCollegeLeague(league: string | null | undefined): boolean {
  if (!league) return false
  const lower = league.toLowerCase()
  return (
    lower.includes('college') || lower.includes('ncaa') || lower.includes('naia') ||
    lower.includes('juco') || lower.includes('njcaa') ||
    lower.includes('fbs') || lower.includes('fcs') ||
    lower.includes('d1') || lower.includes('d2') || lower.includes('d3')
  )
}

/**
 * College levels ESPN indexes (athlete pages, headshots, team schedules).
 * NAIA and JUCO are not among them, so nothing automated exists for those.
 */
export function isEspnCollegeLeague(league: string | null | undefined): boolean {
  if (!isCollegeLeague(league)) return false
  const lower = league!.toLowerCase()
  return !lower.includes('naia') && !lower.includes('juco') && !lower.includes('njcaa')
}

/** Get max games for a league/sport combination */
export function getMaxGames(league: string | null | undefined, sport: string | null | undefined): number {
  const l = (league || '').toLowerCase()
  const s = (sport || '').toLowerCase()

  // Pro leagues
  if (l === 'nfl' || l.includes('nfl')) return 17
  if (l === 'nba' || l.includes('nba')) return 82
  if (l === 'wnba' || l.includes('wnba')) return 40
  if (l === 'mlb' || l.includes('mlb')) return 162
  if (l === 'nhl' || l.includes('nhl')) return 82
  if (l === 'mls' || l.includes('mls')) return 34

  // College by sport
  if (isCollegeLeague(league)) {
    if (s.includes('football')) return 15 // regular + bowl/playoff
    if (s.includes('basketball')) return 35
    if (s.includes('baseball')) return 56
    return 30 // generic college default
  }

  // High school
  if (l.includes('high school')) {
    if (s.includes('football')) return 15
    if (s.includes('basketball')) return 30
    return 20
  }

  // Fallback by sport name alone
  if (s.includes('football')) return 17
  if (s.includes('basketball')) return 82
  if (s.includes('baseball')) return 162
  return 17
}

/** Map entity league field to stats CSV league ('NFL' | 'NCAA') */
export function leagueToStatsLeague(league: string | null | undefined): 'NFL' | 'NCAA' | undefined {
  if (!league) return undefined
  const lower = league.toLowerCase()
  if (lower === 'nfl' || lower.includes('nfl')) return 'NFL'
  if (isEspnCollegeLeague(league)) return 'NCAA'
  return undefined
}
